import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";

import { mealsPlanActions, traineeActions, traineeMealsPlanActions } from "@fitness-app/app-store";
import {
  ClientNutritionStatus,
  type ClientMealsPlanDetails,
  type ClientNutrition,
} from "@fitness-app/data-models/entities/ClientNutrition";
import { type MealsPlan, type MealsPlanDetails } from "@fitness-app/data-models/entities/MealsPlan";
import { type TraineeProfile } from "@fitness-app/data-models/entities/Trainee";
import { generateTraineeMealsPlanDetails } from "@fitness-app/utils/src/nutrition/generateMealsPlanDetails";
import {
  transformAddMealPlanModelToTraineeNutrition,
  type NutrientsTargets,
  type TraineeNutritionFormModel,
} from "@fitness-app/utils/src/nutrition/mealsPlanGenerators";

import { useUserRole } from "~/hooks/trainer/useUserRole";
import { useEntityChange } from "~/hooks/useEntityChange";
import { traineeKeys, useTraineeSearchParams } from "~/modules/Trainee/TraineeProfile/scope";
import { supabase, useAppDispatch } from "~/store/initializeStore";
import { withTraineeStore } from "~/modules/Trainee/TraineeProfile/scope";

export const useAddNutritionToTrainee = (
  type: true | false | "scheduled",
  traineeProfile: TraineeProfile | null,
  onSuccessCallback?: () => void,
) => {
  const dispatch = useAppDispatch();
  const { userId } = useUserRole();

  const [loading, onSuccess, onFailure, onStart] = useEntityChange(() => {
    onSuccessCallback?.();
  });
  const [_searchParams, setSearchParams] = useTraineeSearchParams();
  const queryClient = useQueryClient();

  const addNutritionToTraineeNow = async (
    formData: TraineeNutritionFormModel,
    targets: NutrientsTargets,
    _supplementsFromCurrentPlan?: ClientMealsPlanDetails["supplements"],
    draft?: null | Omit<MealsPlan, "createdBy" | "shared">,
  ) => {
    onStart();
    try {
      let supplements: ClientMealsPlanDetails["supplements"] = [];
      const traineeId = traineeProfile?.id || "";

      if (formData.copySupplementsPlan && traineeProfile?.activeMealsPlanId) {
        const response = await dispatch(
          traineeMealsPlanActions.fetchMealsPlanDetails({ planId: traineeProfile?.activeMealsPlanId }),
        ).unwrap();
        supplements = response?.supplements || [];
      }

      const modelToSave = transformAddMealPlanModelToTraineeNutrition({
        model: formData,
        nutrients: targets,
        traineeId,
        userId,
      });

      let details: MealsPlanDetails | null = null;

      if (formData.selectedTemplate && !draft) {
        const response = await dispatch(
          mealsPlanActions.fetchMealsPlanWithDetails({ id: formData.selectedTemplate }),
        ).unwrap();

        details = response.details[0] || null;
      }

      if (type === "scheduled") {
        const { data } = await supabase.from("client_meals_plan").select("id, name").match({
          traineeId,
          status: ClientNutritionStatus.Scheduled,
          startAt: modelToSave.startAt,
        });

        if (data?.length) {
          onFailure(`Istnieje już plan rozpoczynający się w dniu ${modelToSave.startAt}`);
          return;
        }

        let planId = "";

        if (draft) {
          await dispatch(
            traineeMealsPlanActions.updateMealsPlan({
              mealsPlan: {
                id: draft.id,
                status: ClientNutritionStatus.Scheduled,
                startAt: modelToSave.startAt,
                traineeId,
                name: modelToSave.name || draft.name,
              },
              mealsPlanDetails:
                formData.copySupplementsPlan && supplements?.length
                  ? {
                      planId: draft.id,
                      supplements,
                    }
                  : null,
            }),
          ).unwrap();

          planId = draft.id;
        } else {
          const { id } = await dispatch(
            traineeMealsPlanActions.addScheduledNutritionToTrainee({
              nutrition: modelToSave,
              mealsPlanDetails: generateTraineeMealsPlanDetails(modelToSave, traineeId, details, supplements),
              traineeId,
            }),
          ).unwrap();

          planId = id;
        }

        await queryClient.invalidateQueries({ queryKey: ["scheduledNutrition", traineeId] });
        await queryClient.invalidateQueries({ queryKey: ["archivedNutritionPlans", traineeId] });
        await queryClient.invalidateQueries({ queryKey: ["draftNutritionPlans", traineeId] });
        onSuccess();
        setSearchParams({ planId, tab: "nutrition", nutritionTab: "schedule" });
      } else {
        if (draft) {
          const startAt = dayjs().startOf("day").format("YYYY-MM-DD");
          await dispatch(
            traineeMealsPlanActions.updateMealsPlan({
              mealsPlan: {
                id: draft.id,
                status: ClientNutritionStatus.InProgress,
                startAt,
                // Bez traineeId thunk bierze klientkę z Reduxa po `await` - przy kartach to może być już inna.
                traineeId,
              },
              mealsPlanDetails:
                formData.copySupplementsPlan && supplements?.length
                  ? {
                      planId: draft.id,
                      supplements,
                    }
                  : null,
            }),
          ).unwrap();

          await dispatch(
            traineeActions.updateTrainee({
              id: traineeId,
              profile: {
                activeMealsPlanId: draft.id,
                hasActiveNutrition: formData.hasActiveNutrition ?? false,
                updatedAt: new Date().toISOString(),
              },
              additionalData: {
                nutrition: {
                  name: draft.name,
                  id: draft.id,
                  startAt,
                },
              },
            }),
          );
        } else {
          await dispatch(
            traineeMealsPlanActions.addNutritionToTrainee({
              nutrition: modelToSave,
              mealsPlanDetails: generateTraineeMealsPlanDetails(modelToSave, traineeId, details),
              shareWithClient: formData.hasActiveNutrition ?? false,
            }),
          ).unwrap();
        }

        await queryClient.invalidateQueries({ queryKey: ["archivedNutritionPlans", traineeId] });
        await queryClient.invalidateQueries({ queryKey: ["draftNutritionPlans", traineeId] });
        await queryClient.invalidateQueries({ queryKey: ["scheduledNutrition", traineeId] });
        void queryClient.invalidateQueries({ queryKey: traineeKeys.all(traineeId) });
        onSuccess();

        if (draft) {
          setSearchParams({ planId: draft.id, tab: "nutrition", nutritionTab: "schedule" });
        }
      }
    } catch (e) {
      onFailure();
    }
  };

  const addSelectedNutritionToTraineeNow = async (
    formModel: { hasActiveNutrition: boolean; copySupplementsPlan: boolean },
    selectedNutrition: ClientNutrition,
  ) => {
    let supplements: ClientMealsPlanDetails["supplements"] = [];
    const traineeId = traineeProfile?.id || "";

    if (formModel.copySupplementsPlan && traineeProfile?.activeMealsPlanId) {
      const query = supabase
        .from("client_meals_plan_details")
        .select("*")
        .eq("planId", traineeProfile.activeMealsPlanId);

      const { data } = await query.single<ClientMealsPlanDetails>();

      supplements = data?.supplements || [];
    }

    const startAt = dayjs().startOf("day").format("YYYY-MM-DD");
    await dispatch(
      traineeMealsPlanActions.updateMealsPlan({
        previousNutritionIdToArchive: traineeProfile?.activeMealsPlanId,
        mealsPlan: {
          id: selectedNutrition.id,
          status: ClientNutritionStatus.InProgress,
          startAt,
          traineeId,
          metadata: {
            ...selectedNutrition.metadata,
          },
        },
        mealsPlanDetails:
          formModel.copySupplementsPlan && supplements?.length
            ? {
                planId: selectedNutrition.id,
                supplements,
              }
            : null,
      }),
    ).unwrap();

    await dispatch(
      traineeActions.updateTrainee({
        id: traineeId,
        profile: {
          activeMealsPlanId: selectedNutrition.id,
          hasActiveNutrition: formModel.hasActiveNutrition ?? false,
          updatedAt: new Date().toISOString(),
        },
        additionalData: {
          nutrition: {
            name: selectedNutrition.name,
            id: selectedNutrition.id,
            startAt,
          },
        },
      }),
    );

    await queryClient.invalidateQueries({ queryKey: ["archivedNutritionPlans", traineeId] });
    await queryClient.invalidateQueries({ queryKey: ["draftNutritionPlans", traineeId] });
    await queryClient.invalidateQueries({ queryKey: ["scheduledNutrition", traineeId] });
    void queryClient.invalidateQueries({ queryKey: traineeKeys.all(traineeId) });

    if (selectedNutrition.status === ClientNutritionStatus.Template) {
      setSearchParams({ planId: selectedNutrition.id, tab: "nutrition", nutritionTab: "schedule" });
    }
  };

  // Całe operacje (razem ze sprawdzeniem duplikatów i wczytaniem suplementów) trzymają kolejkę
  // Reduxa: `updateMealsPlan` czyta klientkę i plan ze stanu po `await`, więc w trakcie nie może
  // go przejąć karta innej klientki.
  const addNutritionToTrainee = async (...args: Parameters<typeof addNutritionToTraineeNow>) => {
    try {
      await withTraineeStore(traineeProfile?.id || "", () => addNutritionToTraineeNow(...args));
    } catch {
      onFailure();
    }
  };

  const addSelectedNutritionToTrainee = (...args: Parameters<typeof addSelectedNutritionToTraineeNow>) =>
    withTraineeStore(traineeProfile?.id || "", () => addSelectedNutritionToTraineeNow(...args));

  return {
    loading,
    addNutritionToTrainee,
    addSelectedNutritionToTrainee,
  };
};
